/**
 * Canonical status → color mapping for badges/labels across the app.
 *
 * Every entity status (bookings, payments, invoices, envelopes, exports,
 * identity, subscriptions, emails) resolves to one of six semantic buckets so
 * the same state reads the same color on every screen. Rulings baked in here
 * (see docs/brand-consistency-audit.md):
 *  - a chosen cancellation is neutral, not red — red is for genuine failures
 *  - refunds/returns are reversals (purple), not errors
 *  - "open" (awaiting payment) and anything waiting on a person is warning
 *  - unknown statuses fall back to neutral, never green
 */
export type StatusBucket = 'success' | 'warning' | 'info' | 'error' | 'reversal' | 'neutral';

const STATUS_BUCKETS: Record<string, StatusBucket> = {
    booked: 'success',
    paid: 'success',
    succeeded: 'success',
    complete: 'success',
    completed: 'success',
    stopped: 'success',
    verified: 'success',
    active: 'success',
    sent: 'success',
    available: 'success',

    enabled: 'success',

    pending: 'warning',
    pending_approval: 'warning',
    payment_due: 'warning',
    past_due: 'warning',
    requires_input: 'warning',
    requires_action: 'warning',
    open: 'warning',
    overpaid: 'warning',
    in_progress: 'warning',
    init: 'warning',

    started: 'info',
    processing: 'info',
    reserved: 'info',
    partially_paid: 'info',
    process_deposit: 'info',

    declined: 'error',
    voided: 'error',
    expired: 'error',
    failed: 'error',
    disputed: 'error',
    uncollectible: 'error',

    refunded: 'reversal',
    refund: 'reversal',
    partial_refund: 'reversal',
    manual_refund: 'reversal',
    returned: 'reversal',

    disabled: 'neutral',
    inactive: 'neutral',
    cancelled: 'neutral',
    canceled: 'neutral',
    archived: 'neutral',
    skipped: 'neutral',
    unavailable: 'neutral',
    concept: 'neutral',
    draft: 'neutral',
    reset: 'neutral',
    sold: 'neutral',
    unknown: 'neutral',
};

const BADGE_CLASSES: Record<StatusBucket, string> = {
    success: 'bg-status-success-tint text-status-success-deep ring-1 ring-inset ring-status-success/20 dark:bg-status-success-dark dark:text-status-success-light dark:ring-status-success-light/30',
    warning: 'bg-status-warning-tint text-status-warning-deep ring-1 ring-inset ring-status-warning/20 dark:bg-status-warning-dark dark:text-status-warning-light dark:ring-status-warning-light/30',
    info: 'bg-status-info-tint text-status-info-deep ring-1 ring-inset ring-status-info/20 dark:bg-status-info-dark dark:text-status-info-light dark:ring-status-info-light/30',
    error: 'bg-status-error-tint text-status-error-deep ring-1 ring-inset ring-status-error/20 dark:bg-status-error-dark dark:text-status-error-light dark:ring-status-error-light/30',
    reversal: 'bg-status-reversal-tint text-status-reversal-deep ring-1 ring-inset ring-status-reversal/20 dark:bg-status-reversal-dark dark:text-status-reversal-light dark:ring-status-reversal-light/30',
    neutral: 'bg-status-neutral-tint text-status-neutral-deep ring-1 ring-inset ring-status-neutral/20 dark:bg-status-neutral-dark dark:text-status-neutral-light dark:ring-status-neutral-light/30',
};

const TEXT_CLASSES: Record<StatusBucket, string> = {
    success: 'text-status-success-deep dark:text-status-success-light',
    warning: 'text-status-warning-deep dark:text-status-warning-light',
    info: 'text-status-info-deep dark:text-status-info-light',
    error: 'text-status-error-deep dark:text-status-error-light',
    reversal: 'text-status-reversal-deep dark:text-status-reversal-light',
    neutral: 'text-status-neutral-deep dark:text-status-neutral-light',
};

export function statusBucket(status?: string | null): StatusBucket {
    if (!status) {
        return 'neutral';
    }

    return STATUS_BUCKETS[status.toLowerCase().trim()] ?? 'neutral';
}

/**
 * Orders use stronger lifecycle colors so the most common booking states are
 * visually distinct when scanning the table.
 */
export function orderStatusBucket(status?: string | null): StatusBucket {
    const normalizedStatus = status?.toLowerCase().trim();

    if (normalizedStatus === 'reserved') {
        return 'info';
    }

    if (normalizedStatus === 'started') {
        return 'success';
    }

    if (normalizedStatus === 'stopped' || normalizedStatus === 'returned') {
        return 'reversal';
    }

    if (normalizedStatus === 'completed') {
        return 'warning';
    }

    if (normalizedStatus === 'canceled' || normalizedStatus === 'cancelled') {
        return 'error';
    }

    return statusBucket(normalizedStatus);
}

export function statusBadgeClass(status?: string | null): string {
    return BADGE_CLASSES[statusBucket(status)];
}

export function statusTextClass(status?: string | null): string {
    return TEXT_CLASSES[statusBucket(status)];
}

export function bucketBadgeClass(bucket: StatusBucket): string {
    return BADGE_CLASSES[bucket];
}

export function bucketTextClass(bucket: StatusBucket): string {
    return TEXT_CLASSES[bucket];
}

const ICON_CLASSES: Record<StatusBucket, string> = {
    success: 'bg-status-success text-white',
    warning: 'bg-status-warning text-white',
    info: 'bg-status-info text-white',
    error: 'bg-status-error text-white',
    reversal: 'bg-status-reversal text-white',
    neutral: 'bg-status-neutral text-white',
};

export function statusIconClass(status?: string | null): string {
    return ICON_CLASSES[statusBucket(status)];
}

const VUETIFY_COLORS: Record<StatusBucket, string> = {
    success: 'success',
    warning: 'warning',
    info: 'info',
    error: 'error',
    reversal: 'reversal',
    neutral: 'grey',
};

export function statusVuetifyColor(status?: string | null): string {
    return VUETIFY_COLORS[statusBucket(status)];
}

export function bucketVuetifyColor(bucket: StatusBucket): string {
    return VUETIFY_COLORS[bucket];
}

export function formatStatusLabel(status?: string | null): string {
    if (!status) {
        return '';
    }

    const label = status.replace(/[_-]+/g, ' ').trim();

    return label.charAt(0).toUpperCase() + label.slice(1);
}
